/**
 * @file The app's top bar: logo, page menu, saving state, search, notifications
 * and settings. Consumers: every top-level page. One item list feeds the wide
 * Menu and the narrow Dropdown; Analytics appears only with analytics read
 * rights. Width is `min(100%, 100vw)`, never wider than the viewport on
 * FormPage's max-content page. From `sm` the right group is content-sized and
 * the left group takes the rest from a zero basis, never from its content: the
 * Menu fills it and folds what does not fit, and widens again with the bar.
 * The search asks for its 500px cap from `lg` and for its own width below. Below
 * `sm` the right group takes the free width, the search keeps a
 * `controlHeight * 4` floor and the saving indicator is icon-only. An optional
 * `ref` reaches the outer bar, which FormPage measures for its sticky header.
 */
import { CSSProperties, Ref, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Button, theme, Dropdown, Grid, Badge } from 'antd';
import type { MenuProps } from 'antd';
import { MenuOutlined } from '@ant-design/icons';

import Icon from './atoms/Icon';
import FlexBox from './atoms/FlexBox';
import Search from './atoms/Search';
import SavingIndicator from './atoms/SavingIndicator';
import { ThemeContext } from '../providers/ThemeContext';
import { UserContext } from '../providers/UserContext';
import { devFlag } from '../config';
import { analyticsRights } from '@/analytics';

type NavbProps = {
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
};

export default function Navb({ style, ref }: NavbProps) {
  const { token } = theme.useToken();
  const { useBreakpoint } = Grid;
  const { sm, lg } = useBreakpoint();
  const { darkMode } = useContext(ThemeContext);
  const { notificationBundles, userRights } = useContext(UserContext);

  const { pathname } = useLocation();

  const selectedKey = pathname.split('/')[1].trim() || 'home';

  const x = sm ? 20 : 10;
  const canSeeAnalytics = analyticsRights(userRights).canRead;
  const canSeeIntake =
    devFlag && !!(userRights.aiOperator || userRights.aiAdmin);
  const canSeeLedger = !!(
    userRights.ledgerRead ||
    userRights.ledgerPost ||
    userRights.ledgerCommit
  );

  const menuItems: MenuProps['items'] = [
    {
      key: 'home',
      label: <Link to="/">Home</Link>,
    },
    {
      key: 'forms',
      label: <Link to="/forms">Forms</Link>,
    },
    {
      key: 'entities',
      label: <Link to="/entities">Records</Link>,
    },
    ...(canSeeLedger
      ? [
          {
            key: 'ledger',
            label: <Link to="/ledger">Ledger</Link>,
          },
        ]
      : []),
    ...(canSeeAnalytics
      ? [
          {
            key: 'analytics',
            label: <Link to="/analytics">Analytics</Link>,
          },
        ]
      : []),
    ...(canSeeIntake
      ? [
          {
            key: 'intake',
            label: <Link to="/intake">Intake</Link>,
          },
        ]
      : []),
    {
      key: 'archive',
      label: <Link to="/archive">Archive</Link>,
    },
    ...(devFlag
      ? [
          {
            key: 'dev',
            label: '✶Dev',
            children: [
              {
                key: 'apis',
                label: <Link to="/apis">APIs</Link>,
              },
              {
                key: 'error-testing',
                label: <Link to="/error-testing">Error Testing</Link>,
              },
              {
                key: 'valtio-debug',
                label: <Link to="/valtio-debug">Valtio Debug</Link>,
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <FlexBox
      ref={ref}
      stretch
      noGrow
      style={{
        paddingBlock: 10,
        paddingInline: x,
        width: 'min(100%, 100vw)',
        boxSizing: 'border-box',
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: token.zIndexPopupBase,
        backgroundColor: token.colorBgLayout,
        ...style,
      }}
    >
      <FlexBox
        justifyStart
        noGrow
        gap={x}
        style={sm ? { flex: '1 1 0', minWidth: 0 } : undefined}
      >
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img
            src={
              darkMode
                ? '/art/Syncretic-icon-negative.svg'
                : '/art/Syncretic-icon.svg'
            }
            alt="Syncretic"
            style={{
              height: 36,
            }}
          />
        </Link>
        {sm ? (
          <Menu
            mode="horizontal"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'none',
              border: 'none',
            }}
            selectedKeys={[selectedKey]}
            items={menuItems}
          />
        ) : (
          <Dropdown
            menu={{
              items: menuItems,
              style: {
                width: '100%',
              },
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MenuOutlined />} />
          </Dropdown>
        )}
      </FlexBox>
      <SavingIndicator compact={!sm} />
      <FlexBox justifyEnd noGrow={sm} gap={10} style={{ minWidth: 0 }}>
        <Search
          autoCompleteStyle={
            lg
              ? { width: 500 }
              : sm
                ? undefined
                : { minWidth: token.controlHeight * 4 }
          }
        />
        <Link to="/notifications">
          <Badge count={notificationBundles?.unreadNotifications?.length ?? 0}>
            <Button
              type={selectedKey === 'notifications' ? 'primary' : 'text'}
              icon={<Icon.Bell />}
            />
          </Badge>
        </Link>
        <Link to="/settings">
          <Button
            type={selectedKey === 'settings' ? 'primary' : 'text'}
            icon={<Icon.Settings />}
          />
        </Link>
      </FlexBox>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: x,
          right: x,
          height: '1px',
          backgroundColor: token.colorBorder,
        }}
      />
    </FlexBox>
  );
}
